import React, { useEffect, useState } from "react";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import { Box, Button, Card, CardContent, CircularProgress, Typography } from "@mui/material";
import * as authService from "../../api/auth";

/**
 * React 18 StrictMode runs effects twice in dev. The first verify succeeds and clears the
 * token hash in the DB; the second call then gets 400. Share one in-flight request per token.
 */
const verifyEmailByToken = new Map();

function verifyEmailOnce(token) {
  if (!verifyEmailByToken.has(token)) {
    verifyEmailByToken.set(
      token,
      authService.verifyEmailToken(token).catch((err) => {
        verifyEmailByToken.delete(token);
        throw err;
      })
    );
  }
  return verifyEmailByToken.get(token);
}

function decodeTokenParam(raw) {
  const s = raw.trim();
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const raw = params.get("token");
  const token = raw ? decodeTokenParam(raw) : null;
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Missing verification token.");
      return;
    }
    let cancelled = false;
    verifyEmailOnce(token)
      .then(() => {
        if (!cancelled) {
          setStatus("ok");
          setMessage("Your email is verified. You can sign in and create your farm.");
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setStatus("error");
          const d = e?.response?.data?.detail;
          setMessage(
            (Array.isArray(d) ? d.map((x) => x.msg || x).join(" ") : d) ||
              e?.message ||
              "Verification failed."
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <Box sx={{ display: "flex", justifyContent: "center", pt: 4, px: 2 }}>
      <Card sx={{ maxWidth: 480, width: "100%" }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Email verification
          </Typography>
          {status === "loading" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
              <CircularProgress size={28} />
              <Typography>Verifying…</Typography>
            </Box>
          )}
          {status !== "loading" && (
            <Typography
              color={status === "ok" ? "text.primary" : "error"}
              paragraph
            >
              {message}
            </Typography>
          )}
          <Button component={RouterLink} to="/login" variant="contained">
            Sign in
          </Button>
          {status === "ok" && (
            <Button
              component={RouterLink}
              to="/create-farm"
              sx={{ ml: 1 }}
              variant="outlined"
            >
              Create farm
            </Button>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
